import type { LeadWithRelations } from '@/types';

export type LeadStage = 'new' | 'needs_follow_up' | 'estimate_sent' | 'estimate_accepted' | 'converted' | 'lost';

export interface LeadStageConfig {
  value: LeadStage;
  label: string;
  /** Token colour for the column dot */
  dotClass: string;
  /** Shown under the column header when nobody can drop into it */
  hint?: string;
}

export const LEAD_STAGES: LeadStageConfig[] = [
  { value: 'new', label: 'New', dotClass: 'bg-info' },
  { value: 'needs_follow_up', label: 'Follow-up', dotClass: 'bg-warning' },
  {
    value: 'estimate_sent',
    label: 'Estimate sent',
    dotClass: 'bg-primary',
    hint: 'Leads move here by themselves when an estimate is sent',
  },
  {
    value: 'estimate_accepted',
    label: 'Accepted',
    dotClass: 'bg-success',
    hint: 'The customer accepted the estimate: book the job',
  },
  { value: 'converted', label: 'Booked', dotClass: 'bg-success' },
  { value: 'lost', label: 'Lost', dotClass: 'bg-destructive' },
];

/** Board column for a lead. Legacy status values fold into the nearest column. */
export function stageOf(status: string | null | undefined): LeadStage {
  switch (status) {
    case 'needs_follow_up':
    case 'estimate_sent':
    case 'estimate_accepted':
    case 'converted':
    case 'lost':
      return status;
    case 'quote_sent':
      return 'estimate_sent';
    case 'spam':
      return 'lost';
    default:
      return 'new';
  }
}

/**
 * Where a lead may be moved by hand.
 * - Estimate sent is only reached by sending an estimate (DB trigger on estimates).
 * - Booked is never left from the board: moving a lead out of converted cancels its job.
 * - Lost reopens to New or Follow-up; it is not booked straight from Lost.
 */
const MOVES: Record<LeadStage, LeadStage[]> = {
  new: ['needs_follow_up', 'converted', 'lost'],
  needs_follow_up: ['new', 'converted', 'lost'],
  estimate_sent: ['new', 'needs_follow_up', 'converted', 'lost'],
  estimate_accepted: ['needs_follow_up', 'converted', 'lost'],
  converted: [],
  lost: ['new', 'needs_follow_up'],
};

export function allowedMoves(from: LeadStage): LeadStage[] {
  return MOVES[from];
}

export function canMoveLead(from: LeadStage, to: LeadStage): boolean {
  return MOVES[from].includes(to);
}

const time = (value: string | null | undefined) => (value ? new Date(value).getTime() : NaN);

/**
 * Leads grouped into board columns. Follow-up is ordered by due date (soonest
 * first, undated last); every other column is newest first.
 */
export function groupLeadsByStage(leads: LeadWithRelations[]): Record<LeadStage, LeadWithRelations[]> {
  const groups: Record<LeadStage, LeadWithRelations[]> = {
    new: [],
    needs_follow_up: [],
    estimate_sent: [],
    estimate_accepted: [],
    converted: [],
    lost: [],
  };
  for (const lead of leads) groups[stageOf(lead.status)].push(lead);

  const newestFirst = (a: LeadWithRelations, b: LeadWithRelations) => time(b.created_at) - time(a.created_at);
  for (const stage of Object.keys(groups) as LeadStage[]) {
    if (stage === 'needs_follow_up') {
      groups[stage].sort((a, b) => {
        const da = time(a.next_follow_up_date);
        const db = time(b.next_follow_up_date);
        if (isNaN(da) && isNaN(db)) return newestFirst(a, b);
        if (isNaN(da)) return 1;
        if (isNaN(db)) return -1;
        return da - db;
      });
    } else {
      groups[stage].sort(newestFirst);
    }
  }
  return groups;
}
